/**
 * A component to display a shareable link with a copy-to-clipboard functionality.
 */
import './ShareList.scss';

import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { useIsMounted } from '@ulta/core/hooks/useIsMounted/useIsMounted';
import { CLIENT_ACTION_TYPES } from '@ulta/core/utils/constants/action';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { copyTextToClipboard } from '@ulta/utils/Formatters/Formatters';

export const ShareList = ( props ) => {
  const {
    title,
    subTitle,
    copyAction,
    link,
    copiedText,
    shareLinkMessage
  } = props;

  const [isCopied, setIsCopied] = useState( false );
  const navigatorObj = global.navigator?.clipboard;
  const isMounted = useIsMounted();
  const showCopyButton = copyAction?.label && navigatorObj && isMounted;
  const copyTimeoutRef = useRef( null );

  useEffect( () => {
    return () => {
      clearTimeout( copyTimeoutRef.current );
    };
  }, [] );

  return (
    <div className='ShareList'>
      <div className='ShareList__Content'>
        <div className='ShareList__Header'>
          { title && (
            <div className='ShareList__Title'>
              <Text
                textStyle='title-lg'
                htmlTag='h2'
              >
                { title }
              </Text>
            </div>
          ) }
          { subTitle && (
            <div className='ShareList__Subtitle'>
              <Text textStyle='body-lg'>
                { subTitle }
              </Text>
            </div>
          ) }
          { shareLinkMessage && (
            <div className='ShareList__ShareLinkLabel'>
              <Text textStyle='body'>
                { shareLinkMessage }
              </Text>
            </div>
          ) }
          { link && (
            <div className='ShareList__ShareLink'>
              <Text>{ link }</Text>
            </div>
          ) }
        </div>
        { showCopyButton && (
          <div className='ShareList__Button'>
            <span
              aria-live='polite'
              role='status'
            >
              { isCopied && (
                <Button
                  iconName='check'
                  label={ copiedText }
                  width='fullWidth'
                />
              ) }
              { !isCopied && (
                <Button
                  action={ copyAction }
                  iconName='copy'
                  onClick={ () => copyText( {
                    link,
                    copyAction,
                    setIsCopied,
                    copyTimeoutRef
                  } ) }
                  label={ copyAction.label }
                  width='fullWidth'
                />
              ) }
            </span>
          </div>
        ) }
      </div>
    </div>
  );
};

/**
 * onClick handler for the copy button. Manages clipboard copy and UI state.
 * @param {object} data - The data needed for the copy action.
 * @param {string} data.link - The text/link to copy.
 * @param {object} data.copyAction - The action configuration object.
 * @param {Function} data.setIsCopied - The state setter for the copied status.
 * @param {object} data.copyTimeoutRef - The ref object holding the timer ID.
 */
export const copyText = async( data ) => {
  const { link, copyAction, setIsCopied, copyTimeoutRef } = handleEmptyObjects( data );
  if( !link || !copyAction || !setIsCopied || !copyTimeoutRef ){
    return;
  }

  if( copyAction.clientActionType !== CLIENT_ACTION_TYPES.COPYTEXT_CLICK ){
    await copyTextToClipboard( link );
    setIsCopied( true );

    clearTimeout( copyTimeoutRef.current );

    copyTimeoutRef.current = setTimeout( () => setIsCopied( false ), 5000 );
  }
};

/**
* Property type definitions
  * @typedef ShareListProps
  * @type {object}
  * @property {object} title - The main title of the component.
  * @property {string} subTitle - A subtitle or description displayed below the main title.
  * @property {object} copyAction - Configuration for the action button.
  * @property {string} copiedText - String text for copy button once value is copied
  * @property {string} link - Link for sharing list
  * @property {string} shareLinkMessage - Label for sharing list
*/
export const propTypes = {
  title: PropTypes.shape( {
    text: PropTypes.string.isRequired,
    htmlTag: PropTypes.string
  } ),
  subTitle: PropTypes.string,
  copyAction: PropTypes.shape( {
    label: PropTypes.string.isRequired
  } ).isRequired,
  copiedText: PropTypes.string,
  link: PropTypes.string,
  shareLinkMessage: PropTypes.string
};

ShareList.propTypes = propTypes;
export default ShareList;